/**
 * Dual-spelling record reads: pick the first PRESENT value across the snake_case and camelCase spellings of ONE field,
 * with the coalesce semantics named by the function rather than re-decided at every call site.
 *
 * File storage is camelCase and D1 is snake_case (CLAUDE.md `## Critical Rules`), so a record crossing that boundary
 * carries both spellings of the same field and every reader must coalesce. Four modules hand-rolled that coalesce and
 * the SAME field family diverged between them: `page_url` is read with `||` in `utils/activity/context-appliers.ts`
 * and with `||` but a different fallback tail in `flows/user-activity-operation.ts`, while `session_id` is read with
 * `||` in one of those files and with `??` in the other. The divergence was invisible because each site looked
 * locally correct.
 *
 * Consumers: `utils/activity/context-appliers.ts`, `flows/user-activity-operation.ts`, `flows/property-details.ts`,
 * `components/messaging/messaging-mappers.ts`, `tools/property/user-do-stub-operations.ts`. Paired forbid-rule:
 * `QUAL-DUAL-SPELLING-COALESCE-SSOT-001`.
 *
 * THE RECORD+KEYS FORM IS THE ONLY ONE, and a variadic-over-VALUES coalesce is not a second member of this family: a
 * caller holding a record and a key tuple would have to map one over the other to feed it, which reconstructs
 * {@link firstPresent}'s own arguments at the call site. Falsy-but-present values (`0`, `''`) win here, and a caller
 * needing a different absent-value sentinel composes {@link firstOrValue} rather than a parallel spelling.
 *
 * HOMED IN const/, NOT utils/. `utils/activity/context-appliers.ts` is one of the consumers, and a `utils/` module may
 * value-import only `@federa/const`, `@federa/types` and `zod` (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, whose
 * `UTILS_FLOOR_PACKAGE_PREFIXES` in `utils/compliance/rule-helpers/utils-floor-specifier.ts`
 * carries no allowlist arm) -- so a `utils/` home would be unreachable from the very site that
 * motivated the extraction. Same constraint and same remedy as `const/record-guards.ts` and `const/telemetry-identity.ts`.
 *
 * KEYS ARE PASSED EXPLICITLY, in the call site's own precedence order, and are checked against the record's key set at
 * compile time. There is deliberately NO camelize-the-snake-key derivation: live sites read a THIRD alias that is
 * nobody's twin (`sessionDuration` beside `duration_ms` / `durationMs`), so a derived pair would silently drop it.
 *
 * TWO functions, because the migrated corpus carries exactly TWO semantics and they are NOT interchangeable: `??`
 * skips only null/undefined, `||` additionally skips the empty string. Naming that choice is the whole point of the
 * leaf -- a site that swaps one for the other is making a behaviour change, not a style change.
 *
 * NOT A DUPLICATE OF `normalizeDualKeyField` (`tools/activity/event-schema-payload.ts`), and do not fold the two:
 * these are pure READERS, that one is the WRITE-side normalizer. It takes its two keys from the CLOSED pair
 * vocabulary `ACTIVITY_FIELD_PAIRS` (`const/observability-activity-types.ts`) and throws on any other key, its
 * precedence is fixed snake-then-camel rather than caller-chosen, and its purpose is the UNCONDITIONAL delete of the
 * camel twin -- pinned by `tools/activity/__tests__/event-schema-payload.test.ts`, and without it the twin survives into
 * the activity row's `payload` JSON beside its own canonical column. Neither reader here can express that delete, and
 * substituting `firstPresent` for its internal coalesce would also drop the both-nullish arm it keeps, since `a ?? b`
 * yields `b` while `firstPresent` yields `undefined`. The paired rules split the same way and govern disjoint shapes:
 * `QUAL-DUAL-SPELLING-COALESCE-SSOT-001` fires on a hand-rolled READ expression, `QUAL-DUAL-CASED-FIELD-PAIR-LITERAL-001`
 * on a PRODUCER literal spelling both twins. `flows/property-details.ts` has already taken the first cure and still
 * owes the second, which is what proves them complementary rather than rival.
 *
 * NAME COLLISION, CURED: `const/string-guards.ts` briefly exported a same-named `firstNonEmptyString` and now exports
 * it as `firstTrimmedNonEmptyString`, whose name states the difference. That one is variadic over VALUES, it TRIMS,
 * and it returns `null`; this one takes a RECORD plus key names, does NOT trim, and returns `undefined`. Both live in
 * `const/`, so keep the names distinct -- a bare re-spelling of either silently changes whitespace handling and the
 * absent-value sentinel at every call site. A third sibling, `selectFirstNonEmpty` (`utils/strings/select-first-non-empty.ts`),
 * takes an ARRAY and returns `''`; its docstring carries the full discriminator. The record reader below takes its
 * non-empty test from the untrimmed VALUES coalescer `firstUntrimmedNonEmptyString` (`const/string-guards.ts`), so the
 * two cannot disagree about what counts as empty.
 */

import { firstUntrimmedNonEmptyString } from './string-guards.ts';

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): dual-spelling reader homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. `??` SEMANTICS: the first key whose value is
 * neither `null` nor `undefined` wins, and `undefined` is returned when no key carries one -- so a call site keeps its
 * own `?? <fallback>` tail and reads exactly as the chain it replaces. A falsy-but-present value (`0`, `false`, `''`)
 * WINS here; use {@link firstNonEmptyString} where the replaced chain used `||`.
 */
export function firstPresent<T extends object, K extends keyof T & string>(record: T, ...keys: K[]): T[K] | undefined {
	for (const key of keys) {
		const value = record[key];
		if (value !== null && value !== undefined) return value;
	}
	return undefined;
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the dual-spelled `id`/`ID` of a row that may be absent, read
 * through {@link firstPresent} -- `null` for an absent row and for a row carrying neither spelling. The embed
 * dispatch's transaction id and the save-message sender lookup both read it.
 */
export function dualSpelledIdOrNull(row: Record<string, unknown> | null): unknown {
	return row === null ? null : (firstPresent(row, 'id', 'ID') ?? null);
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): dual-spelling reader homed in const/ so pure utils/ leaves can
 * value-import it without a forbidden cross-utils sibling dependency. `||` SEMANTICS over string fields: the first key
 * carrying a NON-EMPTY string wins; `null`, `undefined`, the empty string and every non-string value are skipped, and
 * `undefined` is returned when no key qualifies.
 *
 * WHITESPACE IS NOT TRIMMED, deliberately. A trim-based emptiness test would be a real behaviour change at every
 * migrated site (`'  '` is truthy, so the `||` chains this replaces accept it), and smuggling a semantic tightening
 * into a mechanical extraction is the failure mode `const/telemetry-identity.ts` documents having made DELIBERATELY
 * and in the open. Tighten here only as its own decision, with the consumers re-read.
 */
export function firstNonEmptyString<T extends object>(record: T, ...keys: (keyof T & string)[]): string | undefined {
	return firstUntrimmedNonEmptyString(...keys.map((key) => record[key])) ?? undefined;
}
